<template>
  <template v-if="canManage">
    <div v-if="loadingEffectiveBeneficiaries" class="flex justify-center py-8">
      <v-progress-circular indeterminate color="primary" width="2" size="24" />
    </div>
    <template v-else>
      <OpeningCard
        :title="t('treezorOnboarding.effectiveBeneficiaries.title')"
        :subtitle="t('treezorOnboarding.effectiveBeneficiaries.hint')"
      >
        <v-alert
          v-if="showMissing && missingRoles.length > 0"
          type="error"
          variant="tonal"
          density="comfortable"
          class="mb-4"
        >
          <p v-for="missingRole in missingRoles" :key="missingRole" class="m-0">
            {{ missingRole }}
          </p>
        </v-alert>
        <v-alert
          v-if="effectiveBeneficiaryItems.length === 0"
          type="info"
          variant="tonal"
          density="comfortable"
          class="mb-4"
        >
          {{ t("treezorOnboarding.effectiveBeneficiaries.empty") }}
        </v-alert>
        <div v-else class="mb-4 flex flex-col gap-3">
          <v-card
            v-for="item in effectiveBeneficiaryItems"
            :key="item.userId"
            variant="outlined"
            rounded="lg"
            link
            class="flex cursor-pointer items-center gap-2 px-3 py-3 border-theme sm:gap-3 sm:px-4"
            @click="editingKey = item.userId"
          >
            <v-icon icon="mdi-account-outline" class="text-medium-emphasis" />
            <div class="flex min-w-0 grow flex-wrap items-center gap-2">
              <span class="text-sm font-medium">{{
                displayName(item.form) || t("treezorOnboarding.effectiveBeneficiaries.unnamed")
              }}</span>
              <span v-if="item.form.sharesPercent" class="text-medium-emphasis tabular-nums">
                {{ `${item.form.sharesPercent}%` }}
              </span>
              <v-chip
                v-if="item.isCompanyRepresentative"
                size="x-small"
                variant="tonal"
                color="primary"
              >
                {{ t("treezorOnboarding.effectiveBeneficiaries.types.COMPANY_REPRESENTATIVE") }}
              </v-chip>
              <v-chip v-if="item.isLegalRepresentative" size="x-small" variant="tonal">
                {{ t("treezorOnboarding.effectiveBeneficiaries.types.LEGAL_REPRESENTATIVE") }}
              </v-chip>
            </div>
            <v-chip
              v-if="item.missingInfo"
              class="flex-none"
              size="small"
              variant="tonal"
              :color="beneficiaryFlagged(item.userId) ? 'error' : 'primary'"
            >
              {{
                beneficiaryFlagged(item.userId)
                  ? t("treezorOnboarding.effectiveBeneficiaries.infoMissing")
                  : t("treezorOnboarding.effectiveBeneficiaries.toComplete")
              }}
            </v-chip>
            <v-icon icon="mdi-chevron-right" class="flex-none text-medium-emphasis" />
          </v-card>
        </div>

        <v-btn
          color="primary"
          variant="tonal"
          prepend-icon="mdi-plus"
          :disabled="effectiveBeneficiariesBusy || !canEditKyc"
          @click="editingKey = 'new'"
        >
          {{ t("treezorOnboarding.effectiveBeneficiaries.add") }}
        </v-btn>
      </OpeningCard>
    </template>

    <div class="mt-6 flex justify-end gap-2">
      <v-btn
        class="me-auto"
        variant="text"
        prepend-icon="mdi-chevron-left"
        :disabled="effectiveBeneficiariesBusy"
        @click="emit('previous')"
      >
        {{ t("common.previous") }}
      </v-btn>
      <v-btn
        color="primary"
        variant="flat"
        append-icon="mdi-chevron-right"
        :disabled="effectiveBeneficiariesBusy"
        @click="emit('next')"
      >
        {{ t("common.next") }}
      </v-btn>
    </div>
  </template>

  <v-dialog :model-value="!!editingItem" width="900" scrollable persistent>
    <v-card v-if="editingItem">
      <v-card-title class="text-xl font-bold">
        {{
          displayName(editingItem.form) ||
          (editingItem.userId === undefined
            ? t("treezorOnboarding.effectiveBeneficiaries.add")
            : t("treezorOnboarding.effectiveBeneficiaries.unnamed"))
        }}
      </v-card-title>
      <v-tabs
        v-if="!editingItem.isAccountCreator"
        v-model="editingTab"
        color="primary"
        class="px-4"
      >
        <v-tab
          v-for="(invalid, tab) in invalidTabs"
          :key="tab"
          :value="tab"
          :color="invalid ? 'error' : 'primary'"
          :base-color="invalid ? 'error' : undefined"
          :append-icon="invalid ? 'mdi-alert-circle' : undefined"
        >
          {{ t(`treezorOnboarding.effectiveBeneficiaries.tabs.${tab}`) }}
        </v-tab>
      </v-tabs>
      <v-divider />
      <v-card-text class="effective-beneficiary-dialog-body overscroll-contain">
        <v-alert
          v-if="editingItem.isAccountCreator"
          type="info"
          variant="tonal"
          density="compact"
          class="mb-6"
        >
          {{ t("treezorOnboarding.effectiveBeneficiaries.representativeIdentityHint") }}
        </v-alert>
        <v-form
          id="effective-beneficiary-form"
          ref="editForm"
          :disabled="editingBusy || !canEditKyc"
          validate-on="invalid-input lazy"
          @submit.prevent="editingTab === 'identity' ? onNextTab() : onSubmitEffectiveBeneficiary()"
        >
          <!-- Eager items keep both tabs mounted, so the form validates every field on save. -->
          <v-tabs-window v-model="editingTab">
            <v-tabs-window-item
              v-if="!editingItem.isAccountCreator"
              value="identity"
              eager
              class="flex flex-col gap-10"
            >
              <section>
                <h3 class="text-lg font-semibold mb-0 leading-tight">
                  {{ t("treezorOnboarding.effectiveBeneficiaries.sections.identity.title") }}
                </h3>
                <p class="text-sm text-medium-emphasis mt-0 mb-6">
                  {{ t("treezorOnboarding.effectiveBeneficiaries.sections.identity.hint") }}
                </p>
                <PersonIdentityFields
                  v-model="editingItem.form"
                  :missing-fields="editingMissingFields"
                />
              </section>
              <section>
                <h3 class="text-lg font-semibold mb-6">
                  {{ t("treezorOnboarding.sections.contact") }}
                </h3>
                <PersonContactFields
                  v-model="editingItem.form"
                  :missing-fields="editingMissingFields"
                />
              </section>
            </v-tabs-window-item>
            <v-tabs-window-item value="details" eager class="flex flex-col gap-10">
              <section>
                <h3 class="text-lg font-semibold mb-0 leading-tight">
                  {{ t("treezorOnboarding.effectiveBeneficiaries.sections.ownership.title") }}
                </h3>
                <p class="text-sm text-medium-emphasis mt-0 mb-6">
                  {{ t("treezorOnboarding.effectiveBeneficiaries.sections.ownership.hint") }}
                </p>
                <v-row :gap="[32, 24]">
                  <v-col cols="12">
                    <v-radio-group
                      v-model="editingItem.form.controllingPersonType"
                      name="controllingPersonType"
                      :rules="[required]"
                      color="primary"
                    >
                      <template #label>
                        {{
                          t(
                            "treezorOnboarding.effectiveBeneficiaries.controllingPersonType.question",
                          )
                        }}
                        <span class="text-error">*</span>
                      </template>
                      <v-radio :value="1">
                        <template #label>
                          <div class="flex flex-col">
                            <span>{{
                              t(
                                "treezorOnboarding.effectiveBeneficiaries.controllingPersonType.shares",
                              )
                            }}</span>
                            <span class="text-sm text-medium-emphasis">{{
                              t(
                                "treezorOnboarding.effectiveBeneficiaries.controllingPersonType.sharesHint",
                              )
                            }}</span>
                          </div>
                        </template>
                      </v-radio>
                      <v-radio :value="2">
                        <template #label>
                          <div class="flex flex-col">
                            <span>{{
                              t(
                                "treezorOnboarding.effectiveBeneficiaries.controllingPersonType.control",
                              )
                            }}</span>
                            <span class="text-sm text-medium-emphasis">{{
                              t(
                                "treezorOnboarding.effectiveBeneficiaries.controllingPersonType.controlHint",
                              )
                            }}</span>
                          </div>
                        </template>
                      </v-radio>
                    </v-radio-group>
                  </v-col>
                  <v-col v-if="!editingItem.isAccountCreator" cols="12">
                    <v-checkbox
                      v-model="editingItem.form.relationshipTypeIds"
                      :value="RelationshipTypeId.LegalRepresentative"
                      hide-details
                      color="primary"
                    >
                      <template #label>
                        <div class="flex flex-col">
                          <span>{{
                            t("treezorOnboarding.effectiveBeneficiaries.isLegalRepresentative")
                          }}</span>
                          <span class="text-sm text-medium-emphasis">{{
                            t("treezorOnboarding.effectiveBeneficiaries.isLegalRepresentativeHint")
                          }}</span>
                        </div>
                      </template>
                    </v-checkbox>
                  </v-col>
                  <v-col v-if="!editingItem.isAccountCreator" cols="12">
                    <v-checkbox
                      v-model="editingItem.form.specifiedUSPerson"
                      hide-details
                      color="primary"
                      @update:model-value="
                        (value) => {
                          if (value && editingItem) editingItem.form.taxResidenceCountry = 'US';
                        }
                      "
                    >
                      <template #label>
                        {{
                          t("treezorOnboarding.effectiveBeneficiaries.specifiedUSPersonQuestion")
                        }}
                        <span class="text-error">*</span>
                      </template>
                    </v-checkbox>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model.trim="editingItem.form.sharesPercent"
                      name="sharesPercent"
                      :rules="[required, nonnegativeNumber, maxNumber(100)]"
                      :error-messages="
                        sharesPercentFlagged && !editingItem.form.sharesPercent
                          ? t('errors.required')
                          : []
                      "
                      :hint="
                        editingItem.form.controllingPersonType === 2
                          ? t('treezorOnboarding.effectiveBeneficiaries.sharesPercentNoSharesHint')
                          : undefined
                      "
                      persistent-hint
                    >
                      <template #label>
                        {{ t("treezorOnboarding.effectiveBeneficiaries.sharesPercent") }}
                        <span class="text-error">*</span>
                      </template>
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-autocomplete
                      v-model="editingItem.form.taxResidenceCountry"
                      name="taxResidenceCountry"
                      autocomplete="tax-residence"
                      :error-messages="
                        taxResidenceFlagged && !editingItem.form.taxResidenceCountry
                          ? t('errors.required')
                          : []
                      "
                      :items="isoCountryItems"
                      :disabled="editingItem.form.specifiedUSPerson || editingBusy || !canEditKyc"
                      clearable
                    >
                      <template #label>
                        {{ t("treezorOnboarding.effectiveBeneficiaries.taxResidenceCountry") }}
                        <span class="text-error">*</span>
                      </template>
                    </v-autocomplete>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model.trim="editingItem.form.taxPayerId"
                      name="taxPayerId"
                      :disabled="
                        !editingItem.form.taxResidenceCountry || editingBusy || !canEditKyc
                      "
                      :error-messages="
                        taxResidenceFlagged &&
                        editingItem.form.taxResidenceCountry &&
                        editingItem.form.taxResidenceCountry !== 'FR' &&
                        !editingItem.form.taxPayerId
                          ? t('errors.required')
                          : []
                      "
                    >
                      <template #label>
                        {{ t("treezorOnboarding.effectiveBeneficiaries.taxPayerId") }}
                        <span
                          v-if="
                            editingItem.form.taxResidenceCountry &&
                            editingItem.form.taxResidenceCountry !== 'FR'
                          "
                          class="text-error"
                          >*</span
                        >
                      </template>
                    </v-text-field>
                  </v-col>
                </v-row>
              </section>

              <section>
                <h3 class="text-lg font-semibold mb-0 leading-tight">
                  {{ t("treezorOnboarding.effectiveBeneficiaries.sections.pep.title") }}
                </h3>
                <p class="text-sm text-medium-emphasis mt-0 mb-6">
                  {{ t("treezorOnboarding.effectiveBeneficiaries.isPepHint") }}
                </p>
                <v-row :gap="[32, 24]">
                  <v-col cols="12">
                    <v-checkbox
                      v-model="editingItem.form.isPep"
                      :label="t('treezorOnboarding.effectiveBeneficiaries.isPep')"
                      hide-details
                      color="primary"
                    />
                  </v-col>
                  <template v-if="editingItem.form.isPep">
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="editingItem.form.occupationCategory"
                        name="occupationCategory"
                        :error-messages="missingMessage('occupationCategory')"
                        :items="
                          occupationCategoryItems.map((value) => ({
                            title: t(
                              `treezorOnboarding.effectiveBeneficiaries.occupationCategories.${value}`,
                            ),
                            value,
                          }))
                        "
                        :rules="[required]"
                      >
                        <template #label>
                          {{ t("treezorOnboarding.effectiveBeneficiaries.occupationCategory") }}
                          <span class="text-error">*</span>
                        </template>
                      </v-select>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="editingItem.form.monthlyIncomeRange"
                        name="monthlyIncomeRange"
                        :error-messages="missingMessage('monthlyIncomeRange')"
                        :items="monthlyIncomeRangeOptions"
                        :rules="[required]"
                      >
                        <template #label>
                          {{ t("treezorOnboarding.effectiveBeneficiaries.monthlyIncomeRange") }}
                          <span class="text-error">*</span>
                        </template>
                      </v-select>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="editingItem.form.personalAssetsRange"
                        name="personalAssetsRange"
                        :error-messages="missingMessage('personalAssetsRange')"
                        :items="personalAssetsRangeOptions"
                        :rules="[required]"
                      >
                        <template #label>
                          {{ t("treezorOnboarding.effectiveBeneficiaries.personalAssetsRange") }}
                          <span class="text-error">*</span>
                        </template>
                      </v-select>
                    </v-col>
                  </template>
                </v-row>
              </section>
            </v-tabs-window-item>
          </v-tabs-window>
        </v-form>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-btn
          v-if="editingItem.userId !== undefined && !editingItem.isAccountCreator"
          color="error"
          variant="tonal"
          :loading="deletingEffectiveBeneficiaryId === editingItem.userId"
          :disabled="editingBusy || !canEditKyc"
          @click="openDeleteEffectiveBeneficiaryConfirm(editingItem.userId)"
        >
          {{ t("common.delete") }}
        </v-btn>
        <v-spacer />
        <v-btn :disabled="editingBusy" @click="closeEditDialog">
          {{ editingItem.userId === undefined ? t("common.cancel") : t("common.close") }}
        </v-btn>
        <v-btn
          v-if="editingTab === 'details' && !editingItem.isAccountCreator"
          variant="text"
          prepend-icon="mdi-chevron-left"
          :disabled="editingBusy"
          @click="editingTab = 'identity'"
        >
          {{ t("common.previous") }}
        </v-btn>
        <v-btn
          v-if="editingTab === 'identity'"
          type="submit"
          form="effective-beneficiary-form"
          color="primary"
          variant="flat"
          append-icon="mdi-chevron-right"
          :disabled="editingBusy"
        >
          {{ t("common.next") }}
        </v-btn>
        <v-btn
          v-else
          type="submit"
          form="effective-beneficiary-form"
          color="primary"
          variant="flat"
          :loading="
            editingItem.userId === undefined
              ? creatingEffectiveBeneficiary
              : updatingEffectiveBeneficiaryId === editingItem.userId
          "
          :disabled="editingBusy || !canEditKyc"
        >
          {{ t("common.save") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog
    v-model="showDeleteEffectiveBeneficiaryDialog"
    width="400"
    :persistent="!!deletingEffectiveBeneficiaryId"
  >
    <v-card>
      <v-card-title>{{
        t("treezorOnboarding.effectiveBeneficiaries.deleteConfirmTitle")
      }}</v-card-title>
      <v-card-text>
        {{
          t("treezorOnboarding.effectiveBeneficiaries.deleteConfirmMessage", {
            name:
              effectiveBeneficiaryToDelete?.name ??
              t("treezorOnboarding.effectiveBeneficiaries.unnamed"),
          })
        }}
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          :disabled="!!deletingEffectiveBeneficiaryId"
          @click="showDeleteEffectiveBeneficiaryDialog = false"
        >
          {{ t("common.cancel") }}
        </v-btn>
        <v-btn
          color="error"
          variant="flat"
          :loading="!!deletingEffectiveBeneficiaryId"
          :disabled="!!deletingEffectiveBeneficiaryId"
          @click="onConfirmDeleteEffectiveBeneficiary"
        >
          {{ t("common.delete") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts" setup>
import { useI18n } from "@/composables/useI18n";
import { useRules } from "@/composables/useRules";
import { call } from "@/firebase";
import { useToast } from "@/store";
import { callables } from "@aircap/common/callables";
import {
  RelationshipTypeId,
  TREEZOR_KYC_PEP_EFFECTIVE_BENEFICIARY_FIELDS,
  TREEZOR_KYC_REQUIRED_EFFECTIVE_BENEFICIARY_FIELDS,
  type User as TreezorUser,
} from "@aircap/common/treezor";
import { captureException } from "@sentry/vue";
import { computed, reactive, ref, watch } from "vue";
import type { VForm } from "vuetify/components";
import OpeningCard from "../OpeningCard.vue";
import PersonContactFields from "../PersonContactFields.vue";
import PersonIdentityFields from "../PersonIdentityFields.vue";
import {
  effectiveBeneficiaryFormFrom,
  effectiveBeneficiaryStepIncomplete,
  emptyEffectiveBeneficiaryForm,
  useTreezorOnboarding,
} from "../useTreezorOnboarding";

const props = defineProps<{ showMissing: boolean }>();
const emit = defineEmits<{ previous: []; next: [] }>();

const { t } = useI18n();
const { required, nonnegativeNumber, maxNumber } = useRules();
const { showSuccessToast, showErrorToast } = useToast();
const {
  accountId,
  canManage,
  canEditKyc,
  effectiveBeneficiaries,
  loadingEffectiveBeneficiaries,
  creatingEffectiveBeneficiary,
  updatingEffectiveBeneficiaryId,
  deletingEffectiveBeneficiaryId,
  effectiveBeneficiaryForms,
  effectiveBeneficiariesBusy,
  accountCreator,
  accountCreatorRelatedBusy,
  kycReviewReadiness,
  checkedBeneficiaryIds,
  loadEffectiveBeneficiaries,
  isoCountryItems,
} = useTreezorOnboarding();

const editForm = ref<VForm>();
const editingKey = ref<number | "new">();
const editingTab = ref<"identity" | "details">("identity");
const revealErrors = ref(false);
const identityChecked = ref(false);
const showDeleteEffectiveBeneficiaryDialog = ref(false);
const effectiveBeneficiaryToDelete = ref<{ userId: number; name: string }>();
const addEffectiveBeneficiaryFormData = reactive(emptyEffectiveBeneficiaryForm());

const occupationCategoryItems = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15,
] as const satisfies readonly NonNullable<TreezorUser["occupationCategory"]>[];

const monthlyIncomeRangeItems = [1, 2, 3, 4, 5, 6, 7] as const satisfies readonly NonNullable<
  TreezorUser["monthlyIncomeRange"]
>[];

const personalAssetsRangeItems = [0, 1, 2, 3, 4, 5, 6, 7] as const satisfies readonly NonNullable<
  TreezorUser["personalAssetsRange"]
>[];

const monthlyIncomeRangeOptions = computed(() =>
  monthlyIncomeRangeItems.map((value) => ({
    title: t(`treezorOnboarding.effectiveBeneficiaries.monthlyIncomeRanges.${value}`),
    value,
  })),
);

const personalAssetsRangeOptions = computed(() =>
  personalAssetsRangeItems.map((value) => ({
    title: t(`treezorOnboarding.effectiveBeneficiaries.personalAssetsRanges.${value}`),
    value,
  })),
);

const displayName = (form: { firstname: string; lastname: string }) =>
  [form.firstname, form.lastname].filter(Boolean).join(" ");

const effectiveBeneficiaryItems = computed(() =>
  effectiveBeneficiaries.value.flatMap((effectiveBeneficiary) => {
    const effectiveBeneficiaryForm =
      effectiveBeneficiaryForms.value[effectiveBeneficiary.user.userId];
    if (!effectiveBeneficiaryForm) return [];
    const isUbo = effectiveBeneficiary.relationships.some(
      (relationship) =>
        relationship.relationshipTypeId === RelationshipTypeId.EffectiveBeneficiaryShares,
    );
    if (!isUbo) return [];
    const incomplete = kycReviewReadiness.value.incompleteEffectiveBeneficiaries.find(
      (person) => person.userId === effectiveBeneficiary.user.userId,
    );
    return [
      {
        userId: effectiveBeneficiary.user.userId,
        isAccountCreator: effectiveBeneficiary.user.userId === accountCreator.value?.userId,
        isCompanyRepresentative: effectiveBeneficiary.relationships.some(
          (relationship) =>
            relationship.relationshipTypeId === RelationshipTypeId.CompanyRepresentative,
        ),
        isLegalRepresentative: effectiveBeneficiary.relationships.some(
          (relationship) =>
            relationship.relationshipTypeId === RelationshipTypeId.LegalRepresentative,
        ),
        form: effectiveBeneficiaryForm,
        missingTaxResidence: !!incomplete?.missingTaxResidence,
        missingSharesPercent: !!incomplete?.missingSharesPercent,
        missingInfo:
          !!incomplete &&
          effectiveBeneficiaryStepIncomplete(incomplete, accountCreator.value?.userId),
      },
    ];
  }),
);

const editingItem = computed(() => {
  if (editingKey.value !== "new")
    return effectiveBeneficiaryItems.value.find((item) => item.userId === editingKey.value);
  return {
    userId: undefined,
    isAccountCreator: false,
    isCompanyRepresentative: false,
    isLegalRepresentative: false,
    form: addEffectiveBeneficiaryFormData,
    missingTaxResidence: false,
    missingSharesPercent: false,
  };
});

const missingRoles = computed(() =>
  kycReviewReadiness.value.missingRelationshipTypeIds.flatMap((relationshipTypeId) => {
    switch (relationshipTypeId) {
      case RelationshipTypeId.LegalRepresentative:
        return [t("treezorOnboarding.effectiveBeneficiaries.missingLegalRepresentative")];
      case RelationshipTypeId.EffectiveBeneficiaryShares:
        return [t("treezorOnboarding.effectiveBeneficiaries.missingEffectiveBeneficiary")];
    }
  }),
);

// The account creator has no identity tab: "Vous" edits their identity and contact details.
watch(editingKey, (key, previousKey) => {
  if (previousKey === "new" && typeof key === "number") return;
  revealErrors.value = false;
  identityChecked.value = false;
  editingTab.value = editingItem.value?.isAccountCreator ? "details" : "identity";
});

const beneficiaryFlagged = (userId: number) =>
  props.showMissing || checkedBeneficiaryIds.has(userId);

const editingSaveChecked = computed(
  () =>
    revealErrors.value ||
    (editingItem.value?.userId !== undefined &&
      checkedBeneficiaryIds.has(editingItem.value.userId)),
);

const editingMissingFields = computed(() => {
  const item = editingItem.value;
  if (!item) return [];
  if (editingSaveChecked.value) {
    const fields = [
      ...(item.isAccountCreator ? [] : TREEZOR_KYC_REQUIRED_EFFECTIVE_BENEFICIARY_FIELDS),
      ...(item.form.isPep ? TREEZOR_KYC_PEP_EFFECTIVE_BENEFICIARY_FIELDS : []),
    ].filter((field) => field !== "specifiedUSPerson");
    return fields.filter((field) => required(item.form[field]) !== true);
  }
  const missing = props.showMissing
    ? (kycReviewReadiness.value.incompleteEffectiveBeneficiaries.find(
        (incompletePerson) => incompletePerson.userId === item.userId,
      )?.missingFields ?? [])
    : [];
  if (!identityChecked.value) return missing;
  return [
    ...missing,
    ...TREEZOR_KYC_REQUIRED_EFFECTIVE_BENEFICIARY_FIELDS.filter(
      (field) => field !== "specifiedUSPerson" && required(item.form[field]) !== true,
    ),
  ];
});

const taxResidenceFlagged = computed(() => {
  const item = editingItem.value;
  if (!item) return false;
  if (editingSaveChecked.value) return true;
  return props.showMissing && item.missingTaxResidence;
});

const sharesPercentFlagged = computed(() => {
  const item = editingItem.value;
  if (!item) return false;
  if (editingSaveChecked.value) return true;
  return props.showMissing && item.missingSharesPercent;
});

const missingMessage = (
  field: "occupationCategory" | "monthlyIncomeRange" | "personalAssetsRange",
) =>
  editingItem.value &&
  editingMissingFields.value.includes(field) &&
  required(editingItem.value.form[field]) !== true
    ? t("errors.required")
    : [];

// Details fields that can be invalid carry a `name`, so form errors use the same keys as
// missing fields. Every other field is on the identity tab.
const detailsTabFields: readonly string[] = [
  "controllingPersonType",
  "sharesPercent",
  "taxResidenceCountry",
  "taxPayerId",
  ...TREEZOR_KYC_PEP_EFFECTIVE_BENEFICIARY_FIELDS,
];

const invalidTabs = computed(() => {
  const item = editingItem.value;
  const invalidFields = [
    ...(editForm.value?.errors.map((error) => error.id.toString()) ?? []),
    // Missing details fields are PEP details: they only count while the PEP box is ticked.
    ...editingMissingFields.value.filter(
      (field) =>
        field !== "specifiedUSPerson" &&
        (item?.form.isPep || !detailsTabFields.includes(field)) &&
        required(item?.form[field]) !== true,
    ),
  ];
  return {
    identity: invalidFields.some((field) => !detailsTabFields.includes(field)),
    details:
      invalidFields.some((field) => detailsTabFields.includes(field)) ||
      (taxResidenceFlagged.value &&
        !!item &&
        (!item.form.taxResidenceCountry ||
          (item.form.taxResidenceCountry !== "FR" && !item.form.taxPayerId))) ||
      (sharesPercentFlagged.value && !!item && !item.form.sharesPercent),
  };
});

const editingBusy = computed(
  () =>
    effectiveBeneficiariesBusy.value ||
    (!!editingItem.value?.isAccountCreator && accountCreatorRelatedBusy.value),
);

const closeEditDialog = () => {
  if (editingKey.value === "new") {
    Object.assign(addEffectiveBeneficiaryFormData, emptyEffectiveBeneficiaryForm());
  } else {
    const effectiveBeneficiary = effectiveBeneficiaries.value.find(
      (item) => item.user.userId === editingKey.value,
    );
    if (effectiveBeneficiary)
      effectiveBeneficiaryForms.value[effectiveBeneficiary.user.userId] =
        effectiveBeneficiaryFormFrom(effectiveBeneficiary);
  }
  editingKey.value = undefined;
};

const onNextTab = async () => {
  identityChecked.value = true;
  await Promise.all(
    editForm.value?.items
      .filter((field) => !detailsTabFields.includes(field.id.toString()))
      .map((field) => field.validate()) ?? [],
  );
  editingTab.value = "details";
};

const onSubmitEffectiveBeneficiary = async () => {
  const item = editingItem.value;
  if (!canEditKyc.value || !item) return;
  if (item.form.specifiedUSPerson) item.form.taxResidenceCountry = "US";
  revealErrors.value = true;
  const result = await editForm.value?.validate();
  if (result && !result.valid && item.userId !== undefined) checkedBeneficiaryIds.add(item.userId);
  const hasValidationErrors =
    result?.valid !== true || invalidTabs.value.identity || invalidTabs.value.details;
  const formatRuleFailed =
    result?.errors.some((error) =>
      error.errorMessages.some((message) => message !== t("errors.required")),
    ) ?? false;

  const knownUserIds =
    item.userId === undefined
      ? new Set(effectiveBeneficiaries.value.map((beneficiary) => beneficiary.user.userId))
      : undefined;

  let taxResidence: { country: string; taxPayerId: string | null } | null = null;
  if (item.form.taxResidenceCountry)
    taxResidence = {
      country: item.form.taxResidenceCountry,
      taxPayerId: item.form.taxPayerId || null,
    };

  if (item.form.controllingPersonType === null) return;
  const payload = {
    accountId: accountId.value,
    specifiedUSPerson: item.form.specifiedUSPerson,
    firstname: item.form.firstname,
    lastname: item.form.lastname,
    middleNames: item.form.middleNames,
    birthday: item.form.birthday,
    placeOfBirth: item.form.placeOfBirth,
    birthCountry: item.form.birthCountry ?? "",
    nationality: item.form.nationality ?? "",
    phone: item.form.phone,
    address1: item.form.address1,
    address2: item.form.address2,
    address3: item.form.address3,
    postcode: item.form.postcode,
    city: item.form.city,
    country: item.form.country ?? "",
    controllingPersonType: item.form.controllingPersonType,
    sharesPercent: item.form.sharesPercent || undefined,
    isLegalRepresentative: item.form.relationshipTypeIds.includes(
      RelationshipTypeId.LegalRepresentative,
    ),
    taxResidence,
    isPep: item.form.isPep,
    occupationCategory: item.form.isPep ? item.form.occupationCategory : undefined,
    monthlyIncomeRange: item.form.isPep ? item.form.monthlyIncomeRange : undefined,
    personalAssetsRange: item.form.isPep ? item.form.personalAssetsRange : undefined,
  };

  let payloadAccepted;
  if (item.userId === undefined)
    payloadAccepted = callables.createTreezorEffectiveBeneficiary.params.safeParse(payload).success;
  else
    payloadAccepted = callables.updateTreezorEffectiveBeneficiary.params.safeParse({
      ...payload,
      userId: item.userId,
    }).success;
  if (formatRuleFailed || !payloadAccepted) {
    if (!formatRuleFailed && !hasValidationErrors) showErrorToast();
    return;
  }

  if (item.userId === undefined) creatingEffectiveBeneficiary.value = true;
  else updatingEffectiveBeneficiaryId.value = item.userId;
  try {
    if (item.userId === undefined) await call("createTreezorEffectiveBeneficiary")(payload);
    else await call("updateTreezorEffectiveBeneficiary")({ ...payload, userId: item.userId });
    await loadEffectiveBeneficiaries({ silent: true });
    if (item.userId === undefined) {
      let createdUserId: number | undefined;
      for (const beneficiary of effectiveBeneficiaries.value)
        if (knownUserIds && !knownUserIds.has(beneficiary.user.userId)) {
          checkedBeneficiaryIds.add(beneficiary.user.userId);
          createdUserId = beneficiary.user.userId;
        }
      if (hasValidationErrors && createdUserId !== undefined) {
        Object.assign(addEffectiveBeneficiaryFormData, emptyEffectiveBeneficiaryForm());
        editingKey.value = createdUserId;
      } else closeEditDialog();
      showSuccessToast(t("treezorOnboarding.effectiveBeneficiaries.createSuccess"));
    } else {
      checkedBeneficiaryIds.add(item.userId);
      if (!hasValidationErrors) closeEditDialog();
      showSuccessToast(t("treezorOnboarding.effectiveBeneficiaries.updateSuccess"));
    }
  } catch (cause) {
    const error = new Error(
      item.userId === undefined
        ? "Error creating Treezor effective beneficiary"
        : "Error updating Treezor effective beneficiary",
      { cause },
    );
    console.error(error);
    captureException(error);
    showErrorToast(cause instanceof Error ? cause.message : undefined);
  } finally {
    creatingEffectiveBeneficiary.value = false;
    updatingEffectiveBeneficiaryId.value = undefined;
  }
};

const openDeleteEffectiveBeneficiaryConfirm = (userId: number) => {
  if (!canEditKyc.value) return;
  const effectiveBeneficiaryForm = effectiveBeneficiaryForms.value[userId];
  effectiveBeneficiaryToDelete.value = {
    userId,
    name:
      (effectiveBeneficiaryForm && displayName(effectiveBeneficiaryForm)) ||
      t("treezorOnboarding.effectiveBeneficiaries.unnamed"),
  };
  showDeleteEffectiveBeneficiaryDialog.value = true;
};

const onConfirmDeleteEffectiveBeneficiary = async () => {
  if (!canEditKyc.value) return;
  const pendingDelete = effectiveBeneficiaryToDelete.value;
  if (!pendingDelete) return;

  deletingEffectiveBeneficiaryId.value = pendingDelete.userId;
  try {
    await call("deleteTreezorEffectiveBeneficiary")({
      accountId: accountId.value,
      userId: pendingDelete.userId,
    });
    await loadEffectiveBeneficiaries({ silent: true, replaceUserIds: [pendingDelete.userId] });
    showDeleteEffectiveBeneficiaryDialog.value = false;
    effectiveBeneficiaryToDelete.value = undefined;
    if (editingKey.value === pendingDelete.userId) editingKey.value = undefined;
    showSuccessToast(t("treezorOnboarding.effectiveBeneficiaries.deleteSuccess"));
  } catch (cause) {
    const error = new Error("Error deleting Treezor effective beneficiary", { cause });
    console.error(error);
    captureException(error);
    showErrorToast(cause instanceof Error ? cause.message : undefined);
  } finally {
    deletingEffectiveBeneficiaryId.value = undefined;
  }
};
</script>

<style scoped>
/* Unlayered on purpose: Vuetify transitions live in the last `vuetify-final` layer. */
.effective-beneficiary-dialog-body :deep(.slide-y-transition-move) {
  transition: none;
}
</style>
